/**
 * Generic ingestion types — file uploads, sheet-link uploads, saved column
 * mapping templates, and manual spend entries.
 *
 * Mirrors WAVE-G1-DESIGN.md §5/§8 and the fixed API contract for Wave G1
 * Sonnet S4: backend/src/models/generic_ingestion.py + the (not-yet-wired)
 * upload/mapping/manual-spend routes described in the slice brief. Field
 * names track the SQLAlchemy models exactly so the uploads/mappings/entries
 * history lists render real columns without guessing.
 */

export type UploadKind = 'csv' | 'sheet_link';

export type UploadStatus =
  | 'received'
  | 'mapped'
  | 'validated'
  | 'landed'
  | 'failed';

export type TargetKind = 'spend' | 'orders' | 'metrics';

// Locked to fact_ad_spend's currency contract (design §5) — the backend
// never falls back to USD for uploads, so the frontend must not invent
// options beyond this exact set either.
export const SUPPORTED_CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'AUD',
  'JPY',
  'CNY',
] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

/** One row-addressed validation failure (HTTP 422 payload shape, design §5). */
export interface RowError {
  row_number: number;
  field: string;
  code: string;
  message: string;
}

/** POST /api/uploads response (both the CSV and sheet-link variants). */
export interface UploadCreated {
  upload_id: string;
  kind: UploadKind;
  columns: string[];
  // Preview rows are keyed by column name in the common case; a defensive
  // fallback for a positional (array-of-arrays) shape lives in the page's
  // sampleValueFor() helper since the contract does not pin this down.
  preview_rows: Array<Record<string, unknown> | unknown[]>;
  /** Column map of the matched template. Kept for compatibility;
   * suggested_template carries the parts this one cannot express. */
  suggested_mapping: Record<string, string> | null;
  /** The WHOLE matched template (design §5: templates re-apply on
   * re-upload). defaults commonly holds the fixed platform label and
   * target_kind decides which validator runs, so re-applying the column
   * map alone silently drops both. */
  suggested_template: SuggestedTemplate | null;
  status: UploadStatus;
}

/** A saved per-tenant mapping template matched to an upload's headers. */
export interface SuggestedTemplate {
  template_id: string;
  name: string;
  target_kind: TargetKind;
  column_map: Record<string, string>;
  /** Matches the mapping form's own state shape: fixed target values are
   * label strings or the is_credit boolean. */
  defaults: Record<string, string | boolean>;
}

/** POST /api/uploads/{id}/map success body (200). */
export interface MapLanded {
  status: 'landed';
  rows_landed: number;
}

/** GET /api/uploads list item. */
export interface UploadSummary {
  id: string;
  kind: UploadKind;
  source_ref: string;
  status: UploadStatus;
  rows_received: number;
  rows_landed: number;
  rows_rejected: number;
  error_summary?: string | null;
  created_at: string;
}

export interface UploadsListResponse {
  uploads: UploadSummary[];
}

/** GET /api/upload-mappings list item — saved per-tenant templates. */
export interface MappingTemplate {
  id: string;
  name: string;
  target_kind: TargetKind;
  column_map: Record<string, string>;
  defaults: Record<string, unknown>;
  created_at: string;
}

export interface UploadMappingsListResponse {
  mappings: MappingTemplate[];
}

/** Manual spend entry as returned by the API (GET/POST/PUT). */
export interface ManualSpendEntryRecord {
  id: string;
  channel_label: string;
  period_start: string;
  period_end: string;
  grain: string;
  amount: number;
  is_credit: boolean;
  currency: string;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ManualSpendListResponse {
  entries: ManualSpendEntryRecord[];
}

/** POST/PUT /api/manual-spend request body. */
export interface ManualSpendInput {
  channel_label: string;
  period_start: string;
  period_end: string;
  amount: number;
  currency: string;
  is_credit?: boolean;
  notes?: string;
}

// --- Target field catalog for the mapping step -----------------------------

export type FieldInputKind = 'text' | 'date' | 'number' | 'currency' | 'boolean';

export interface TargetFieldDef {
  field: string;
  label: string;
  required: boolean;
  inputKind: FieldInputKind;
}

/** TARGET FIELDS by target_kind (slice brief, verbatim). */
export const TARGET_FIELDS: Record<TargetKind, TargetFieldDef[]> = {
  spend: [
    { field: 'platform_label', label: 'Platform label', required: true, inputKind: 'text' },
    { field: 'period_start', label: 'Period start', required: true, inputKind: 'date' },
    { field: 'period_end', label: 'Period end', required: false, inputKind: 'date' },
    { field: 'spend', label: 'Spend', required: true, inputKind: 'number' },
    { field: 'currency', label: 'Currency', required: true, inputKind: 'currency' },
    { field: 'is_credit', label: 'Is credit', required: false, inputKind: 'boolean' },
    { field: 'impressions', label: 'Impressions', required: false, inputKind: 'number' },
    { field: 'clicks', label: 'Clicks', required: false, inputKind: 'number' },
    { field: 'conversions', label: 'Conversions', required: false, inputKind: 'number' },
    { field: 'conversion_value', label: 'Conversion value', required: false, inputKind: 'number' },
  ],
  orders: [
    { field: 'order_date', label: 'Order date', required: true, inputKind: 'date' },
    { field: 'orders', label: 'Orders', required: true, inputKind: 'number' },
    { field: 'revenue', label: 'Revenue', required: true, inputKind: 'number' },
    { field: 'currency', label: 'Currency', required: true, inputKind: 'currency' },
  ],
  metrics: [
    { field: 'metric_name', label: 'Metric name', required: true, inputKind: 'text' },
    { field: 'period_start', label: 'Period start', required: true, inputKind: 'date' },
    { field: 'period_end', label: 'Period end', required: false, inputKind: 'date' },
    { field: 'value', label: 'Value', required: true, inputKind: 'number' },
    { field: 'unit', label: 'Unit', required: false, inputKind: 'text' },
  ],
};

/** Orders and Metrics land and are contract-tested, but no mart or fact
 * reads them yet — Spend is the only kind wired through to the warehouse in
 * Wave G1. Saying so on the option keeps "Landed N rows" from reading as
 * "your numbers moved". */
export const TARGET_KIND_OPTIONS: { label: string; value: TargetKind }[] = [
  { label: 'Spend', value: 'spend' },
  { label: 'Orders (stored, not yet in reports)', value: 'orders' },
  { label: 'Metrics (stored, not yet in reports)', value: 'metrics' },
];
